import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, Loader2, CheckCircle, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, { email });
      setSubmitStatus('success');
      setEmail('');
    } catch (error) {
      console.error('Newsletter signup error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 sm:py-16 relative" data-testid="newsletter-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-5 sm:p-8 md:p-10"
        >
          <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8">
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-3 sm:mb-4">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                <span className="text-xs sm:text-sm font-mono tracking-widest uppercase text-primary">Newsletter</span>
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3">
                Stay Updated
              </h3>
              <p className="text-slate-400 text-sm sm:text-base">
                Get the latest pricing tips, industry insights, and app updates delivered to your inbox.
              </p>
            </div>

            <div className="flex-1 w-full">
              {submitStatus === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center justify-center gap-3 p-4 sm:p-6 bg-[#10B981]/10 rounded-xl border border-[#10B981]/20"
                  data-testid="newsletter-success"
                >
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#10B981]" />
                  <span className="text-[#10B981] font-medium text-sm sm:text-base">You're subscribed!</span>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-500" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="Enter your email"
                      className="pl-10 sm:pl-12 bg-white/5 border-white/10 focus:border-primary h-12 sm:h-14 text-white placeholder:text-slate-500 text-sm sm:text-base"
                      data-testid="newsletter-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary h-12 sm:h-14 px-6 sm:px-8"
                    data-testid="newsletter-submit-btn"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    ) : (
                      'Subscribe'
                    )}
                  </Button>
                </form>
              )}

              {submitStatus === 'error' && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-red-400 text-xs sm:text-sm mt-3 text-center"
                  data-testid="newsletter-error"
                >
                  Something went wrong. Please try again.
                </motion.p>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default NewsletterSection;
